import { useFlags } from 'launchdarkly-react-client-sdk';

import useSpecialMenuCards from './use-special-menu-cards';

/**
 * Decides whether to show the Specials entry in the nav.
 *
 * Why: the legacy signal (`hasProductsOnSpecial` from `menuFiltersV2`) loops over a
 * capped page of filtered products to decide if any are on a special. That misses
 * eligibility-restricted specials and goes stale when product count exceeds the cap.
 * The ARMA-backed `getSpecialMenuCards` endpoint already returns eligibility-filtered
 * menu cards, which is exactly what the Specials page renders — so gating the nav on
 * that source matches what the destination will actually show.
 *
 * The same flag also suppresses the resolver-side product loop in `menuFiltersV2Impl`
 * so we don't pay for both signals during rollout, and the legacy path retires
 * together with the flag at GA.
 */
export function useSpecialsNavVisibility(legacyHasProductsOnSpecial: boolean | undefined): boolean {
  const flags = useFlags();
  const useMenuCards = Boolean(flags['core.cats-ecomm.specials-nav-from-menu-cards.rollout']);
  const { hasSpecials } = useSpecialMenuCards({ skip: !useMenuCards });
  if (!useMenuCards) {
    return legacyHasProductsOnSpecial ?? false;
  }
  return hasSpecials;
}
